import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ShoppingBag, Menu, X, User, LogOut, Heart, Search } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Furniture", to: "/shop?category=Furniture" },
  { label: "Lamps & Lights", to: "/shop?category=Lighting" },
  {
    label: "Decor",
    to: "/shop?category=Decor",
    children: [
      { label: "Wall Decor", to: "/shop?category=Decor&sub=Wall Decor" },
      { label: "Kitchen", to: "/shop?category=Decor&sub=Kitchen" },
      { label: "Living Room Decor", to: "/shop?category=Decor&sub=Living Room Decor" },
    ],
  },
  { label: "Kitchen", to: "/shop?category=Kitchen" },
  { label: "Home Safety", to: "/shop?category=Home Safety" },
  { label: "About Us", to: "/#story" },
  { label: "Track Order", to: "/track" },
  { label: "Contact Us", to: "/contact" },
];

export const Navbar = () => {
  const { count, setOpen } = useCart();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [term, setTerm] = useState("");
  const { pathname } = useLocation();

  const submitSearch = (e) => {
    e?.preventDefault();
    const q = term.trim();
    if (!q) {
      setSearchOpen(false);
      return;
    }
    setMenuOpen(false);
    setSearchOpen(false);
    setTerm("");
    navigate(`/shop?search=${encodeURIComponent(q)}`);
  };

  const goToHash = (to) => {
    const id = to.slice(2);
    if (pathname === "/") {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate(to);
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 border-b border-black/10 bg-bone/90 backdrop-blur-xl">
      <div className="mx-auto flex max-w-[1600px] items-center justify-between px-6 py-4 sm:px-10">
        {pathname !== "/auth" && (
          <Link to="/" data-testid="nav-logo" className="flex items-center">
            <img src="/logo.webp" alt="Shop Online New York" className="h-12 w-auto mix-blend-multiply" />
          </Link>
        )}
        <nav className="hidden items-center gap-8 text-xs uppercase tracking-[0.18em] text-ash lg:flex">
          {NAV.map((item) =>
            item.to.startsWith("/#") ? (
              <button
                key={item.label}
                data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                onClick={() => goToHash(item.to)}
                className="uppercase tracking-[0.18em] transition-colors duration-300 hover:text-charcoal"
              >
                {item.label}
              </button>
            ) : item.children ? (
              <div key={item.label} className="group relative">
                <Link
                  to={item.to}
                  data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="transition-colors duration-300 hover:text-charcoal"
                >
                  {item.label}
                </Link>
                <div className="invisible absolute left-1/2 top-full -translate-x-1/2 pt-5 opacity-0 transition-all duration-300 group-hover:visible group-hover:opacity-100">
                  <div className="min-w-[170px] rounded-2xl border border-charcoal/10 bg-bone p-3 shadow-[0_20px_50px_rgba(22,40,63,0.12)]">
                    {item.children.map((child) => (
                      <Link
                        key={child.label}
                        to={child.to}
                        data-testid={`nav-decor-sub-${child.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        className="block rounded-lg px-4 py-2.5 text-[11px] uppercase tracking-[0.18em] text-ash transition-colors duration-300 hover:bg-charcoal hover:text-bone"
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <Link
                key={item.label}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="transition-colors duration-300 hover:text-charcoal"
              >
                {item.label}
              </Link>
            )
          )}
        </nav>
        <div className="flex items-center gap-3">
          <form onSubmit={submitSearch} className="flex items-center gap-2" data-testid="nav-search">
            {searchOpen && (
              <input
                autoFocus
                data-testid="nav-search-input"
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                placeholder="Search pieces..."
                className="w-36 rounded-full border border-charcoal/20 bg-transparent px-4 py-2 text-xs font-light text-charcoal outline-none transition-colors duration-300 placeholder:text-ash focus:border-charcoal sm:w-52"
              />
            )}
            <button
              type={searchOpen && term.trim() ? "submit" : "button"}
              data-testid="nav-search-button"
              onClick={() => !searchOpen && setSearchOpen(true)}
              className="rounded-full border border-charcoal/20 p-2.5 transition-colors duration-300 hover:bg-charcoal hover:text-bone"
              aria-label="Search"
            >
              <Search size={15} />
            </button>
          </form>
          <Link to="/account?tab=wishlist" data-testid="nav-wishlist"
            className="rounded-full border border-charcoal/20 p-2.5 transition-colors duration-300 hover:bg-charcoal hover:text-bone"
            aria-label="Wishlist">
            <Heart size={15} />
          </Link>
          {user ? (
            <div className="hidden items-center gap-3 sm:flex" data-testid="nav-user">
              <Link to={user.role === "admin" ? "/admin" : "/account"} data-testid="nav-account" className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-charcoal transition-colors duration-300 hover:text-taupe">
                <User size={14} className="text-taupe" /> {user.name?.split(" ")[0] || "Account"}
              </Link>
              <button data-testid="nav-logout" onClick={logout}
                className="flex items-center gap-1 text-xs uppercase tracking-[0.18em] text-ash transition-colors duration-300 hover:text-charcoal">
                <LogOut size={13} /> Sign out
              </button>
            </div>
          ) : (
            <Link to="/auth" data-testid="nav-signin"
              className="hidden text-xs uppercase tracking-[0.18em] text-ash transition-colors duration-300 hover:text-charcoal sm:block">
              Sign in
            </Link>
          )}
          <button
            data-testid="nav-cart-button"
            onClick={() => setOpen(true)}
            className="relative flex items-center gap-2 rounded-full border border-charcoal/20 px-5 py-2 text-xs uppercase tracking-[0.2em] transition-colors duration-300 hover:bg-charcoal hover:text-bone"
          >
            <ShoppingBag size={15} strokeWidth={1.5} />
            Cart
            {count > 0 && (
              <span data-testid="nav-cart-count" className="flex h-5 w-5 items-center justify-center rounded-full bg-moss text-[10px] text-bone">
                {count}
              </span>
            )}
          </button>
          <button
            data-testid="mobile-menu-button"
            onClick={() => setMenuOpen((v) => !v)}
            className="rounded-full border border-charcoal/20 p-2.5 transition-colors duration-300 hover:bg-charcoal hover:text-bone lg:hidden"
            aria-label="Open menu"
          >
            {menuOpen ? <X size={16} /> : <Menu size={16} />}
          </button>
        </div>
      </div>
      {menuOpen && (
        <nav data-testid="mobile-menu" className="border-t border-charcoal/10 bg-bone px-6 py-6 lg:hidden">
          <form onSubmit={submitSearch} className="mb-6 flex items-center gap-2" data-testid="mobile-search">
            <input
              data-testid="mobile-search-input"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="Search pieces..."
              className="flex-1 rounded-full border border-charcoal/20 bg-transparent px-4 py-2.5 text-sm font-light text-charcoal outline-none placeholder:text-ash focus:border-charcoal"
            />
            <button type="submit" data-testid="mobile-search-button"
              className="rounded-full border border-charcoal/20 p-2.5 transition-colors duration-300 hover:bg-charcoal hover:text-bone"
              aria-label="Search">
              <Search size={15} />
            </button>
          </form>
          <div className="grid grid-cols-2 gap-x-8 gap-y-5 text-sm uppercase tracking-[0.18em] text-charcoal">
            {user ? (
              <button data-testid="mobile-nav-signout" onClick={() => { setMenuOpen(false); logout(); }}
                className="text-left uppercase tracking-[0.18em] text-moss">
                Sign out ({user.name?.split(" ")[0] || "account"})
              </button>
            ) : (
              <Link to="/auth" data-testid="mobile-nav-signin" onClick={() => setMenuOpen(false)}>
                Sign in
              </Link>
            )}
            {NAV.map((item) =>
              item.to.startsWith("/#") ? (
                <button
                  key={item.label}
                  data-testid={`mobile-nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  onClick={() => { setMenuOpen(false); goToHash(item.to); }}
                  className="text-left uppercase tracking-[0.18em]"
                >
                  {item.label}
                </button>
              ) : (
                <div key={item.label} className="contents">
                  <Link
                    to={item.to}
                    data-testid={`mobile-nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    onClick={() => setMenuOpen(false)}
                  >
                    {item.label}
                  </Link>
                  {item.children?.map((child) => (
                    <Link
                      key={child.label}
                      to={child.to}
                      data-testid={`mobile-nav-decor-sub-${child.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      onClick={() => setMenuOpen(false)}
                      className="pl-4 text-[11px] normal-case tracking-[0.12em] text-ash"
                    >
                      ↳ {child.label}
                    </Link>
                  ))}
                </div>
              )
            )}
          </div>
        </nav>
      )}
    </header>
  );
};
